import { PUZZLE_COLOR_LEVELS } from "../../pgn/PgnParser";
import { TopicAssignmentFactory, } from "../../types/assignment";
import { PuzzleAttemptFactory, PuzzleStatus, QuizResultFactory, } from "../../types/quiz";
/**
 * Firestore implementation of ProgressionService.
 * Handles storage and retrieval of quiz results and puzzle attempts.
 * Uses subcollections under each user: /progressions/{userId}/quizResults and /progressions/{userId}/puzzleAttempts
 */
export class ProgressionServiceFirestore {
    firestore;
    static PROGRESSIONS_COLLECTION = "progressions";
    static QUIZ_RESULTS_SUBCOLLECTION = "quizResults";
    static PUZZLE_ATTEMPTS_SUBCOLLECTION = "puzzleAttempts";
    static TOPIC_ASSIGNMENTS_SUBCOLLECTION = "topicAssignments";
    constructor(firestore) {
        this.firestore = firestore;
    }
    /**
     * Get the quiz results collection reference for a specific user
     */
    getQuizResultsCollection(userId) {
        // Ensure userId is a string (defensive check for runtime type safety)
        const userIdStr = String(userId);
        return this.firestore
            .collection(ProgressionServiceFirestore.PROGRESSIONS_COLLECTION)
            .doc(userIdStr)
            .collection(ProgressionServiceFirestore.QUIZ_RESULTS_SUBCOLLECTION);
    }
    /**
     * Get the puzzle attempts collection reference for a specific user
     */
    getPuzzleAttemptsCollection(userId) {
        // Ensure userId is a string (defensive check for runtime type safety)
        const userIdStr = String(userId);
        return this.firestore
            .collection(ProgressionServiceFirestore.PROGRESSIONS_COLLECTION)
            .doc(userIdStr)
            .collection(ProgressionServiceFirestore.PUZZLE_ATTEMPTS_SUBCOLLECTION);
    }
    // ============ Helper Methods ============
    toQuizResult(doc) {
        const data = doc.data();
        return {
            id: doc.id,
            playerId: data.playerId,
            studentName: data.studentName,
            topicId: data.topicId,
            topicTitle: data.topicTitle,
            lectureId: data.lectureId,
            classroomId: data.classroomId,
            year: data.year,
            term: data.term,
            startedAt: data.startedAt?.toDate(),
            completedAt: data.completedAt?.toDate(),
            totalScenarios: data.totalScenarios,
            correctCount: data.correctCount,
            incorrectCount: data.incorrectCount,
            skippedCount: data.skippedCount,
            puzzleResults: data.puzzleResults || [],
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
        };
    }
    toPuzzleAttempt(doc) {
        const data = doc.data();
        return {
            id: doc.id,
            playerId: data.playerId,
            studentName: data.studentName,
            scenario: data.scenario, // Full Scenario object (includes topicId and topicTitle)
            scenarioId: data.scenario?.id || data.scenarioId, // For backward compatibility
            scenarioTitle: data.scenario?.title || data.scenarioTitle,
            pgn: data.scenario?.pgn || data.pgn,
            status: data.status,
            answerPgn: data.answerPgn,
            attemptCount: data.attemptCount,
            timeSpent: data.timeSpent,
            quizResultId: data.quizResultId,
            lectureId: data.lectureId,
            classroomId: data.classroomId,
            year: data.year,
            term: data.term,
            attemptedAt: data.attemptedAt?.toDate(),
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
        };
    }
    prepareQuizResultForFirestore(quizResult) {
        return {
            playerId: quizResult.playerId,
            studentName: quizResult.studentName || null,
            topicId: quizResult.topicId || null,
            topicTitle: quizResult.topicTitle || null,
            lectureId: quizResult.lectureId || null,
            classroomId: quizResult.classroomId || null,
            year: quizResult.year || null,
            term: quizResult.term || null,
            startedAt: this.firestore.timestamp.fromDate(quizResult.startedAt),
            completedAt: quizResult.completedAt
                ? this.firestore.timestamp.fromDate(quizResult.completedAt)
                : null,
            totalScenarios: quizResult.totalScenarios,
            correctCount: quizResult.correctCount,
            incorrectCount: quizResult.incorrectCount,
            skippedCount: quizResult.skippedCount,
            puzzleResults: quizResult.puzzleResults,
            createdAt: this.firestore.fieldValue.serverTimestamp(),
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
    }
    preparePuzzleAttemptForFirestore(attempt) {
        // Helper function to convert to Firestore Timestamp
        const toTimestamp = (date) => {
            if (!date)
                return null;
            if (date instanceof Date) {
                return this.firestore.timestamp.fromDate(date);
            }
            // If it's already a Firestore Timestamp, return as is
            if (date.toDate && typeof date.toDate === "function") {
                return date;
            }
            // Try to parse as date string
            try {
                return this.firestore.timestamp.fromDate(new Date(date));
            }
            catch (e) {
                console.warn("Could not convert date:", date, e);
                return null;
            }
        };
        // Serialize the Scenario object for Firestore (includes topicId and topicTitle)
        const scenarioForFirestore = attempt.scenario
            ? {
                id: attempt.scenario.id,
                topicId: attempt.scenario.topicId,
                topicTitle: attempt.scenario.topicTitle || null,
                title: attempt.scenario.title,
                isPuzzle: attempt.scenario.isPuzzle,
                isHomework: attempt.scenario.isHomework,
                pgn: attempt.scenario.pgn,
                status: attempt.scenario.status,
                difficultyLevel: attempt.scenario.difficultyLevel || null,
                order: attempt.scenario.order,
                puzzleLevel: attempt.scenario.puzzleLevel || null,
                createdAt: toTimestamp(attempt.scenario.createdAt),
                updatedAt: toTimestamp(attempt.scenario.updatedAt),
            }
            : null;
        return {
            playerId: attempt.playerId,
            studentName: attempt.studentName || null,
            scenario: scenarioForFirestore,
            status: attempt.status,
            answerPgn: attempt.answerPgn,
            attemptCount: attempt.attemptCount,
            timeSpent: attempt.timeSpent || null,
            quizResultId: attempt.quizResultId || null,
            lectureId: attempt.lectureId || null,
            classroomId: attempt.classroomId || null,
            year: attempt.year || null,
            term: attempt.term || null,
            attemptedAt: toTimestamp(attempt.attemptedAt),
            createdAt: this.firestore.fieldValue.serverTimestamp(),
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
    }
    // ============ Quiz Results Operations ============
    async saveQuizResult(quizResultData) {
        const quizResult = QuizResultFactory.create(quizResultData);
        const quizForFirestore = this.prepareQuizResultForFirestore(quizResult);
        await this.getQuizResultsCollection(quizResult.playerId)
            .doc(quizResult.id)
            .set(quizForFirestore);
        if (quizResult.topicId) {
            await this.updateAssignmentMetrics(quizResult.playerId, quizResult.topicId, {
                quizPersonalBest: quizResult.correctCount,
                quizAttempts: 1,
            });
        }
        return quizResult;
    }
    async getQuizResult(playerId, quizResultId) {
        const doc = await this.getQuizResultsCollection(playerId)
            .doc(quizResultId)
            .get();
        if (!doc.exists) {
            return null;
        }
        return this.toQuizResult(doc);
    }
    async getQuizResultsByStudent(playerId, limit = 50) {
        const snapshot = await this.getQuizResultsCollection(playerId)
            .orderBy("startedAt", "desc")
            .limit(limit)
            .get();
        return snapshot.docs.map((doc) => this.toQuizResult(doc));
    }
    async getQuizResultsByTopic(topicId, limit = 50) {
        // Note: Querying across subcollections requires collection group queries
        const snapshot = await this.firestore
            .collectionGroup(ProgressionServiceFirestore.QUIZ_RESULTS_SUBCOLLECTION)
            .where("topicId", "==", topicId)
            .orderBy("startedAt", "desc")
            .limit(limit)
            .get();
        return snapshot.docs.map((doc) => this.toQuizResult(doc));
    }
    async getQuizResultsByLecture(lectureId) {
        // Note: Querying across subcollections requires collection group queries
        const snapshot = await this.firestore
            .collectionGroup(ProgressionServiceFirestore.QUIZ_RESULTS_SUBCOLLECTION)
            .where("lectureId", "==", lectureId)
            .get();
        return snapshot.docs.map((doc) => this.toQuizResult(doc));
    }
    async getQuizResultsByClassroom(classroomId, year, term) {
        // Note: Querying across subcollections requires collection group queries
        let query = this.firestore
            .collectionGroup(ProgressionServiceFirestore.QUIZ_RESULTS_SUBCOLLECTION)
            .where("classroomId", "==", classroomId);
        if (year) {
            query = query.where("year", "==", year);
        }
        if (term) {
            query = query.where("term", "==", term);
        }
        const snapshot = await query.get();
        return snapshot.docs.map((doc) => this.toQuizResult(doc));
    }
    // ============ Puzzle Attempts Operations ============
    async savePuzzleAttempt(attemptData) {
        const attempt = PuzzleAttemptFactory.create(attemptData);
        // Save the attempt
        const attemptForFirestore = this.preparePuzzleAttemptForFirestore(attempt);
        await this.getPuzzleAttemptsCollection(attempt.playerId)
            .doc(attempt.id)
            .set(attemptForFirestore);
        // Update assignment metrics if this puzzle is for an assigned topic
        if (attempt.scenario?.topicId &&
            attempt.scenario?.isHomework &&
            !attempt.quizResultId // Only count standalone puzzles, not quiz puzzles
        ) {
            try {
                // Get the assignment to check completed puzzle IDs
                const assignment = await this.getTopicAssignment(attempt.playerId, attempt.scenario.topicId);
                if (!assignment) {
                    return attempt;
                }
                const completedPuzzleIds = assignment.completedPuzzleIds || [];
                const isNewCompletion = attempt.status === PuzzleStatus.Correct &&
                    !completedPuzzleIds.includes(attempt.scenarioId);
                // Always increment puzzle attempts
                await this.updateAssignmentMetrics(attempt.playerId, attempt.scenario.topicId, { puzzleAttempts: 1 });
                // If this is a new correct completion, add to completed list
                if (isNewCompletion) {
                    const assignmentRef = this.getTopicAssignmentsCollection(attempt.playerId).doc(attempt.scenario.topicId);
                    // Use a transaction to ensure atomicity and correct count
                    try {
                        await this.firestore.runTransaction(async (transaction) => {
                            const assignmentDoc = await transaction.get(assignmentRef);
                            if (!assignmentDoc.exists) {
                                throw new Error(`Assignment document not found during transaction for player ${attempt.playerId}, topic ${attempt.scenario.topicId}`);
                            }
                            const currentData = assignmentDoc.data();
                            const currentCompletedPuzzleIds = currentData.completedPuzzleIds || [];
                            // Check again if this is still a new completion (in case of race condition)
                            if (!currentCompletedPuzzleIds.includes(attempt.scenarioId)) {
                                const updatedCompletedPuzzleIds = [
                                    ...currentCompletedPuzzleIds,
                                    attempt.scenarioId,
                                ];
                                transaction.update(assignmentRef, {
                                    completedPuzzleIds: updatedCompletedPuzzleIds,
                                    completedPuzzles: updatedCompletedPuzzleIds.length,
                                    updatedAt: this.firestore.fieldValue.serverTimestamp(),
                                });
                            }
                        });
                    }
                    catch (transactionError) {
                        console.error(`Transaction failed to update completed puzzles for player ${attempt.playerId}, topic ${attempt.scenario.topicId}, puzzle ${attempt.scenarioId}:`, transactionError);
                        // Don't throw - we still want to return the attempt even if the update fails
                    }
                }
            }
            catch (error) {
                console.error(`Error updating assignment metrics for puzzle attempt ${attempt.id}:`, error);
                // Don't throw - we still want to return the attempt even if the update fails
            }
        }
        return attempt;
    }
    async savePuzzleAttemptsBatch(attemptsData) {
        if (attemptsData.length === 0) {
            return [];
        }
        // Group attempts by playerId for batch operations
        const attemptsByPlayer = new Map();
        const allAttempts = [];
        for (const attemptData of attemptsData) {
            const attempt = PuzzleAttemptFactory.create(attemptData);
            allAttempts.push(attempt);
            if (!attemptsByPlayer.has(attempt.playerId)) {
                attemptsByPlayer.set(attempt.playerId, []);
            }
            attemptsByPlayer.get(attempt.playerId).push(attempt);
        }
        const batch = this.firestore.batch();
        for (const [playerId, attempts] of attemptsByPlayer.entries()) {
            for (const attempt of attempts) {
                const attemptForFirestore = this.preparePuzzleAttemptForFirestore(attempt);
                const docRef = this.getPuzzleAttemptsCollection(playerId).doc(attempt.id);
                batch.set(docRef, attemptForFirestore);
            }
        }
        await batch.commit();
        return allAttempts;
    }
    async getPuzzleAttempt(playerId, attemptId) {
        const doc = await this.getPuzzleAttemptsCollection(playerId)
            .doc(attemptId)
            .get();
        if (!doc.exists) {
            return null;
        }
        return this.toPuzzleAttempt(doc);
    }
    async getPuzzleAttemptsByStudent(playerId, limit = 50) {
        const snapshot = await this.getPuzzleAttemptsCollection(playerId)
            .orderBy("attemptedAt", "desc")
            .limit(limit)
            .get();
        return snapshot.docs.map((doc) => this.toPuzzleAttempt(doc));
    }
    async hasStudentCompletedPuzzle(playerId, scenarioId) {
        const snapshot = await this.getPuzzleAttemptsCollection(playerId)
            .where("scenarioId", "==", scenarioId)
            .where("status", "==", PuzzleStatus.Correct)
            .limit(1)
            .get();
        return !snapshot.empty;
    }
    async getCompletedPuzzlesByStudent(playerId) {
        const snapshot = await this.getPuzzleAttemptsCollection(playerId)
            .where("status", "==", PuzzleStatus.Correct)
            .orderBy("attemptedAt", "desc")
            .get();
        return snapshot.docs.map((doc) => this.toPuzzleAttempt(doc));
    }
    async getPuzzleAttemptsByScenario(scenarioId) {
        // Note: Querying across subcollections requires collection group queries
        const snapshot = await this.firestore
            .collectionGroup(ProgressionServiceFirestore.PUZZLE_ATTEMPTS_SUBCOLLECTION)
            .where("scenarioId", "==", scenarioId)
            .get();
        return snapshot.docs.map((doc) => this.toPuzzleAttempt(doc));
    }
    async getUncompletedScenarioIds(playerId, scenarioIds) {
        if (scenarioIds.length === 0) {
            return [];
        }
        // Get all completed puzzles for the player
        const completedPuzzles = await this.getCompletedPuzzlesByStudent(playerId);
        const completedScenarioIds = new Set(completedPuzzles.map((p) => p.scenarioId));
        // Filter out completed scenarios
        return scenarioIds.filter((id) => !completedScenarioIds.has(id));
    }
    async getStudentProgressByTopic(playerId, topicId) {
        const snapshot = await this.getPuzzleAttemptsCollection(playerId)
            .where("topicId", "==", topicId)
            .orderBy("attemptedAt", "desc")
            .get();
        const attempts = snapshot.docs.map((doc) => this.toPuzzleAttempt(doc));
        if (attempts.length === 0) {
            return {
                playerId,
                topicId,
                topicTitle: "",
                totalScenarios: 0,
                completedScenarios: 0,
                correctScenarios: 0,
                completionRate: 0,
                successRate: 0,
                attempts: [],
            };
        }
        // Get topic title from first attempt's scenario (if available)
        const topicTitle = attempts[0].scenario?.title || "";
        const uniqueScenarios = new Set(attempts.map((a) => a.scenarioId));
        const correctScenarios = new Set(attempts
            .filter((a) => a.status === PuzzleStatus.Correct)
            .map((a) => a.scenarioId));
        return {
            playerId,
            studentName: attempts[0].studentName,
            topicId,
            topicTitle,
            totalScenarios: uniqueScenarios.size,
            completedScenarios: correctScenarios.size,
            correctScenarios: correctScenarios.size,
            completionRate: (correctScenarios.size / uniqueScenarios.size) * 100,
            successRate: (correctScenarios.size / uniqueScenarios.size) * 100,
            attempts,
            lastAttemptedAt: attempts[0].attemptedAt,
        };
    }
    // ============ Analytics & Reports Operations ============
    async getStudentProgressSummary(playerId, topicService // Optional TopicService to get total puzzles per topic
    ) {
        const snapshot = await this.getPuzzleAttemptsCollection(playerId)
            .orderBy("attemptedAt", "desc")
            .get();
        const attempts = snapshot.docs.map((doc) => this.toPuzzleAttempt(doc));
        // Get quiz results for quiz statistics
        const quizResults = await this.getQuizResultsByStudent(playerId, 1000);
        const totalQuizzesTaken = quizResults.length;
        const averageQuizScore = totalQuizzesTaken > 0
            ? quizResults.reduce((sum, quiz) => {
                const score = quiz.totalScenarios > 0
                    ? (quiz.correctCount / quiz.totalScenarios) * 100
                    : 0;
                return sum + score;
            }, 0) / totalQuizzesTaken
            : 0;
        if (attempts.length === 0) {
            return {
                playerId,
                totalPuzzlesAttempted: 0,
                totalPuzzlesCorrect: 0,
                totalPuzzlesIncorrect: 0,
                totalPuzzlesSkipped: 0,
                totalQuizzesTaken,
                averageQuizScore,
                byTopic: {},
                recentAttempts: [],
            };
        }
        const studentName = attempts[0].studentName;
        const correct = attempts.filter((a) => a.status === PuzzleStatus.Correct);
        const incorrect = attempts.filter((a) => a.status === PuzzleStatus.Incorrect);
        const skipped = attempts.filter((a) => a.status === PuzzleStatus.Skipped);
        // Calculate by topic
        const byTopic = {};
        const topicMap = new Map();
        for (const attempt of attempts) {
            const topicId = attempt.scenario?.topicId;
            if (topicId) {
                if (!topicMap.has(topicId)) {
                    topicMap.set(topicId, []);
                }
                topicMap.get(topicId).push(attempt);
            }
        }
        // Get puzzle counts: use homeworkCount for assigned topics, puzzleCount for others
        const topicPuzzleCounts = new Map();
        if (topicMap.size > 0) {
            try {
                const assignments = await this.getStudentAssignments(playerId);
                const assignedTopicIds = new Set(assignments.filter((a) => a.isActive).map((a) => a.topicId));
                const topicIds = Array.from(topicMap.keys());
                if (topicService) {
                    const topics = await topicService.getTopicsByIds(topicIds);
                    const topicsById = new Map(topics.map((t) => [t.id, t]));
                    for (const topicId of topicIds) {
                        const topic = topicsById.get(topicId);
                        if (!topic)
                            continue;
                        if (assignedTopicIds.has(topicId)) {
                            // Homework topic: use homeworkCount if available, otherwise count homework scenarios
                            if (topic.homeworkCount !== undefined) {
                                topicPuzzleCounts.set(topicId, topic.homeworkCount);
                            }
                            else {
                                const scenarios = await topicService.getScenarios(topicId);
                                const hwCount = scenarios.filter((s) => s.isHomework).length;
                                topicPuzzleCounts.set(topicId, hwCount);
                            }
                        }
                        else {
                            // Not assigned as homework: use total puzzle count
                            if (topic.puzzleCount !== undefined) {
                                topicPuzzleCounts.set(topicId, topic.puzzleCount);
                            }
                            else {
                                const scenarios = await topicService.getScenarios(topicId);
                                const puzzleCount = scenarios.filter((s) => s.isPuzzle).length;
                                topicPuzzleCounts.set(topicId, puzzleCount);
                            }
                        }
                    }
                }
            }
            catch (error) {
                console.error("Error fetching topic puzzle counts:", error);
            }
        }
        for (const [topicId, topicAttempts] of topicMap.entries()) {
            const topicCorrect = topicAttempts.filter((a) => a.status === PuzzleStatus.Correct);
            // Calculate unique puzzles attempted and completed
            const uniqueScenarioIds = new Set(topicAttempts.map((a) => a.scenarioId).filter((id) => id));
            const completedScenarioIds = new Set(topicCorrect.map((a) => a.scenarioId).filter((id) => id));
            const attemptedPuzzles = uniqueScenarioIds.size;
            const completedPuzzles = completedScenarioIds.size;
            const totalPuzzles = topicPuzzleCounts.get(topicId) || attemptedPuzzles;
            const notAttemptedPuzzles = Math.max(0, totalPuzzles - attemptedPuzzles);
            byTopic[topicId] = {
                topicTitle: topicAttempts[0].scenario?.topicTitle || "Unknown Topic",
                totalPuzzles,
                completedPuzzles,
                attemptedPuzzles,
                notAttemptedPuzzles,
                attempted: topicAttempts.length,
                correct: topicCorrect.length,
            };
        }
        return {
            playerId,
            studentName,
            totalPuzzlesAttempted: attempts.length,
            totalPuzzlesCorrect: correct.length,
            totalPuzzlesIncorrect: incorrect.length,
            totalPuzzlesSkipped: skipped.length,
            totalQuizzesTaken,
            averageQuizScore,
            byTopic,
            recentAttempts: attempts.slice(0, 20),
            lastAttemptedAt: attempts[0].attemptedAt,
        };
    }
    async getPuzzleStatistics(scenarioId) {
        const attempts = await this.getPuzzleAttemptsByScenario(scenarioId);
        if (attempts.length === 0) {
            return {
                scenarioId,
                scenarioTitle: "",
                topicId: "",
                topicTitle: "",
                totalAttempts: 0,
                uniqueStudents: 0,
                correctAttempts: 0,
                incorrectAttempts: 0,
                skippedAttempts: 0,
                successRate: 0,
                averageAttempts: 0,
            };
        }
        const uniqueStudents = new Set(attempts.map((a) => a.playerId));
        const correct = attempts.filter((a) => a.status === PuzzleStatus.Correct);
        const incorrect = attempts.filter((a) => a.status === PuzzleStatus.Incorrect);
        const skipped = attempts.filter((a) => a.status === PuzzleStatus.Skipped);
        const totalAttemptCount = attempts.reduce((sum, a) => sum + a.attemptCount, 0);
        const attemptsWithTime = attempts.filter((a) => a.timeSpent !== undefined);
        const totalTime = attemptsWithTime.reduce((sum, a) => sum + (a.timeSpent || 0), 0);
        return {
            scenarioId,
            scenarioTitle: attempts[0].scenarioTitle,
            topicId: attempts[0].scenario?.topicId || "",
            topicTitle: attempts[0].scenario?.title || "Unknown Topic",
            totalAttempts: attempts.length,
            uniqueStudents: uniqueStudents.size,
            correctAttempts: correct.length,
            incorrectAttempts: incorrect.length,
            skippedAttempts: skipped.length,
            successRate: (correct.length / attempts.length) * 100,
            averageAttempts: totalAttemptCount / attempts.length,
            averageTimeSpent: attemptsWithTime.length > 0
                ? totalTime / attemptsWithTime.length
                : undefined,
            difficultyLevel: attempts[0].scenario?.difficultyLevel,
            puzzleLevel: attempts[0].scenario?.puzzleLevel
                ? PUZZLE_COLOR_LEVELS[attempts[0].scenario.puzzleLevel]
                : undefined,
        };
    }
    // ============ Topic Assignment Operations ============
    /**
     * Get the topic assignments collection reference for a specific user
     */
    getTopicAssignmentsCollection(userId) {
        const userIdStr = String(userId);
        return this.firestore
            .collection(ProgressionServiceFirestore.PROGRESSIONS_COLLECTION)
            .doc(userIdStr)
            .collection(ProgressionServiceFirestore.TOPIC_ASSIGNMENTS_SUBCOLLECTION);
    }
    toTopicAssignment(doc) {
        const data = doc.data();
        return {
            id: doc.id,
            playerId: data.playerId,
            studentName: data.studentName,
            topicId: data.topicId,
            topicTitle: data.topicTitle,
            teacherId: data.teacherId,
            teacherName: data.teacherName,
            lectureId: data.lectureId,
            classroomId: data.classroomId,
            year: data.year,
            term: data.term,
            assignedAt: data.assignedAt?.toDate(),
            isActive: data.isActive !== undefined ? data.isActive : true,
            totalPuzzles: data.totalPuzzles || 0,
            quizPersonalBest: data.quizPersonalBest || 0,
            quizAttempts: data.quizAttempts || 0,
            puzzleAttempts: data.puzzleAttempts || 0,
            completedPuzzles: data.completedPuzzles || 0,
            completedPuzzleIds: data.completedPuzzleIds || [],
            videoWatchCount: data.videoWatchCount || 0,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
        };
    }
    prepareTopicAssignmentForFirestore(assignment) {
        // Validate required fields
        if (!assignment.teacherId) {
            throw new Error("teacherId is required for topic assignment");
        }
        return {
            playerId: assignment.playerId,
            studentName: assignment.studentName || null,
            topicId: assignment.topicId,
            topicTitle: assignment.topicTitle,
            teacherId: assignment.teacherId,
            teacherName: assignment.teacherName || null,
            lectureId: assignment.lectureId || null,
            classroomId: assignment.classroomId || null,
            year: assignment.year || null,
            term: assignment.term || null,
            assignedAt: this.firestore.timestamp.fromDate(assignment.assignedAt),
            isActive: assignment.isActive !== undefined ? assignment.isActive : true,
            totalPuzzles: assignment.totalPuzzles || 0,
            quizPersonalBest: assignment.quizPersonalBest || 0,
            quizAttempts: assignment.quizAttempts || 0,
            puzzleAttempts: assignment.puzzleAttempts || 0,
            completedPuzzles: assignment.completedPuzzles || 0,
            completedPuzzleIds: assignment.completedPuzzleIds || [],
            createdAt: this.firestore.fieldValue.serverTimestamp(),
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
    }
    async assignTopic(assignmentData) {
        // Use topicId as document ID to enforce uniqueness constraint
        // This ensures one assignment per playerId+topicId automatically
        const docRef = this.getTopicAssignmentsCollection(assignmentData.playerId).doc(assignmentData.topicId);
        // Check if assignment already exists
        const existingDoc = await docRef.get();
        if (existingDoc.exists) {
            // Assignment exists - update metadata fields, preserve progress fields
            const updateData = {
                studentName: assignmentData.studentName,
                topicTitle: assignmentData.topicTitle,
                teacherId: assignmentData.teacherId,
                teacherName: assignmentData.teacherName,
                lectureId: assignmentData.lectureId,
                classroomId: assignmentData.classroomId,
                year: assignmentData.year,
                term: assignmentData.term,
                assignedAt: assignmentData.assignedAt,
                isActive: assignmentData.isActive,
                totalPuzzles: assignmentData.totalPuzzles,
                updatedAt: new Date(),
                // Progress fields NOT updated (preserved):
                // - completedPuzzles
                // - quizPersonalBest
                // - quizAttempts
                // - puzzleAttempts
                // - completedPuzzleIds
                // - videoWatchCount
            };
            await docRef.update(updateData);
            // Return the updated assignment
            const updatedDoc = await docRef.get();
            return this.toTopicAssignment(updatedDoc);
        }
        else {
            // New assignment - create with all fields
            const assignment = TopicAssignmentFactory.create(assignmentData, assignmentData.topicId // Use topicId as the document ID
            );
            const assignmentForFirestore = this.prepareTopicAssignmentForFirestore(assignment);
            await docRef.set(assignmentForFirestore);
            return assignment;
        }
    }
    async getStudentAssignments(playerId) {
        const snapshot = await this.getTopicAssignmentsCollection(playerId)
            .orderBy("assignedAt", "desc")
            .get();
        return snapshot.docs.map((doc) => this.toTopicAssignment(doc));
    }
    async getTopicAssignment(playerId, topicId) {
        // Document ID is topicId, so we can get it directly
        const doc = await this.getTopicAssignmentsCollection(playerId)
            .doc(topicId)
            .get();
        if (!doc.exists) {
            return null;
        }
        return this.toTopicAssignment(doc);
    }
    async updateAssignmentMetrics(playerId, topicId, metrics) {
        // Document ID is topicId, so we can update directly without querying
        const assignmentRef = this.getTopicAssignmentsCollection(playerId).doc(topicId);
        // Check if assignment exists
        const doc = await assignmentRef.get();
        if (!doc.exists) {
            return; // No assignment found, nothing to update
        }
        const currentData = doc.data();
        const updateData = {
            updatedAt: this.firestore.fieldValue.serverTimestamp(),
        };
        // Update isActive status
        if (metrics.isActive !== undefined) {
            updateData.isActive = metrics.isActive;
        }
        // Only update quiz personal best if the new score is higher
        if (metrics.quizPersonalBest !== undefined) {
            const currentBest = currentData.quizPersonalBest || 0;
            if (metrics.quizPersonalBest > currentBest) {
                updateData.quizPersonalBest = metrics.quizPersonalBest;
            }
        }
        // Increment quiz attempts count
        if (metrics.quizAttempts !== undefined) {
            updateData.quizAttempts = this.firestore.fieldValue.increment(metrics.quizAttempts);
        }
        // Increment puzzle attempts count
        if (metrics.puzzleAttempts !== undefined) {
            updateData.puzzleAttempts = this.firestore.fieldValue.increment(metrics.puzzleAttempts);
        }
        // Set completed puzzles count (not increment, since it's derived from array length)
        if (metrics.completedPuzzles !== undefined) {
            updateData.completedPuzzles = metrics.completedPuzzles;
        }
        // Increment video watch count
        if (metrics.videoWatchCount !== undefined) {
            updateData.videoWatchCount = this.firestore.fieldValue.increment(metrics.videoWatchCount);
        }
        // Only update if there are fields to update besides updatedAt
        if (Object.keys(updateData).length > 1) {
            await assignmentRef.update(updateData);
        }
    }
    async hasTopicAssignment(playerId, topicId) {
        // Document ID is topicId, so we can check existence directly
        const doc = await this.getTopicAssignmentsCollection(playerId)
            .doc(topicId)
            .get();
        return doc.exists;
    }
    async getAssignmentsByStudentsAndTopics(playerIds, topicIds) {
        const assignmentsMap = new Map();
        // Firestore 'in' queries support up to 10 values, so we need to batch
        const BATCH_SIZE = 10;
        // Query by playerId for better long-term scalability:
        // - Short term: 3 queries (30 players / 10 batch) vs 1 query (3 topics)
        // - Long term: Topics accumulate 100s-1000s of players over years (unbounded)
        // - Players have bounded workload (~30-50 topics max)
        // - After 1 year: topicId query fetches 1000+ docs, playerId query fetches ~40 docs per player
        // - 3 queries × 40 docs = 120 docs vs 1 query × 1000 docs = 1000 docs
        // - Better to pay 3x query cost now than 10x data cost later!
        for (let i = 0; i < playerIds.length; i += BATCH_SIZE) {
            const playerIdBatch = playerIds.slice(i, i + BATCH_SIZE);
            // Query by player to avoid fetching all historical assignments for popular topics
            const snapshot = await this.firestore
                .collectionGroup(ProgressionServiceFirestore.TOPIC_ASSIGNMENTS_SUBCOLLECTION)
                .where("playerId", "in", playerIdBatch)
                .get();
            snapshot.docs.forEach((doc) => {
                const assignment = this.toTopicAssignment(doc);
                // Only include assignments for the requested topics
                if (topicIds.includes(assignment.topicId)) {
                    const key = `${assignment.playerId}-${assignment.topicId}`;
                    assignmentsMap.set(key, assignment);
                }
            });
        }
        return assignmentsMap;
    }
    async getAssignmentsByLecture(lectureId) {
        const assignmentsMap = new Map();
        // Query by lectureId is most efficient for modal display:
        // - 1 query only (no batching needed)
        // - Bounded data: 1 lecture × 30 students × 3 topics = ~90 docs max
        // - No stale data growth: each lecture is isolated
        // - Perfect for AssignHomeworkModal context
        const snapshot = await this.firestore
            .collectionGroup(ProgressionServiceFirestore.TOPIC_ASSIGNMENTS_SUBCOLLECTION)
            .where("lectureId", "==", lectureId)
            .get();
        snapshot.docs.forEach((doc) => {
            const assignment = this.toTopicAssignment(doc);
            const key = `${assignment.playerId}-${assignment.topicId}`;
            assignmentsMap.set(key, assignment);
        });
        return assignmentsMap;
    }
    // ============ Batch Assignment Operations ============
    async assignTopicsToStudents(params) {
        const { students, topics, teacher, lecture } = params;
        // Filter students who have chessSquadUsername
        const eligibleStudents = students.filter((s) => s.chessSquadUsername);
        if (eligibleStudents.length === 0) {
            return { assignCount: 0, studentCount: 0 };
        }
        // Extract teacher info
        const teacherId = teacher.teacherId;
        const teacherName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
        // Create assignment operations
        const operations = [];
        for (const student of eligibleStudents) {
            for (const topic of topics) {
                operations.push(this.assignTopic({
                    playerId: student.chessSquadUsername,
                    studentName: `${student.firstName} ${student.lastName}`.trim(),
                    topicId: topic.id,
                    topicTitle: topic.title,
                    teacherId,
                    teacherName,
                    lectureId: lecture.id,
                    classroomId: lecture.classroomId,
                    year: lecture.year,
                    term: lecture.term,
                    assignedAt: new Date(),
                    isActive: true,
                    totalPuzzles: topic.homeworkCount || 0,
                }));
            }
        }
        // Execute all assignment operations in parallel
        await Promise.all(operations);
        return {
            assignCount: operations.length,
            studentCount: eligibleStudents.length,
        };
    }
    async unassignTopicsFromStudents(assignments) {
        const operations = [];
        for (const { playerId, topicId } of assignments) {
            operations.push(this.updateAssignmentMetrics(playerId, topicId, {
                isActive: false,
            }));
        }
        await Promise.all(operations);
        return {
            unassignCount: operations.length,
        };
    }
}
